import { forwardRef } from 'react'

import { BodyXS } from '@/components/atomic/text'

import { Input as BaseInput } from './base'

const inputSizes = {
  // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
  ['small']: 'h-[28px]',
  // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
  ['large']: 'h-[32px]',
}

const separatorSizes = {
  // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
  ['small']: '26px',
  // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
  ['large']: '30px',
}

export interface InputProps
  // We are currently using non-standard size prop
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  placeholder: string
  label?: string
  caption?: string
  error?: boolean
  errorMessage?: string
  size: 'small' | 'large'
  /** Optional props for the slot, which is the icon, icon dropdown, label, or label dropdown on the left or right side of the input */
  slotProps?: SlotProps
  width?: 'full' | string
  wrapperClassName?: string
  // TODO [ENG-3211]: Add slot props for icon and label dropdown once selector component is implemented
}

export interface SlotProps {
  label?: string
  side: 'left' | 'right' | 'both'
  /** If unspecified, the height of the input container will be used, minus the borders */
  separatorHeight?: string
  showSeparator?: boolean
  size?: 'small' | 'large'
  className?: string
  containerClassName?: string
  icon?: React.ReactNode
  // slots on both sides
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  rightLabel?: string
  leftLabel?: string

  step?: string | number
}

/**
 * @deprecated Use `Input` from `@moment/design-system-ui/input` instead.
 */
const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    autoFocus,
    size,
    slotProps,
    width = 'full',
    errorMessage,
    wrapperClassName,
    className,
    defaultValue,
    ...restProps
  } = props
  return (
    <div className={`${width === 'full' ? 'w-full' : width}`}>
      {props.label && (
        <div className="mb-1">
          <BodyXS color={'text-secondary'}>
            {props.label}
            {props.required && <span className="text-moment-red-700">*</span>}
          </BodyXS>
        </div>
      )}
      <BaseInput
        ref={ref}
        defaultValue={defaultValue}
        autoFocus={autoFocus || false}
        wrapperClassName={wrapperClassName}
        className={`${inputSizes[size]} ${className}`}
        placeholderSize={size}
        width={width}
        slotProps={
          slotProps
            ? {
                ...slotProps,
                separatorHeight: separatorSizes[size],
                showSeparator: slotProps.showSeparator ?? true,
                size,
              }
            : undefined
        }
        autoComplete={props.autoComplete}
        onPaste={props.onPaste}
        onWheel={(e) => {
          // Do not allow number inputs to scroll through values
          e.currentTarget.blur()
          e.stopPropagation()
        }}
        {...restProps}
      />
      {props.caption && !props.error && (
        <BodyXS color={'text-secondary'} className="mt-1 pl-[1px]">
          {props.caption}
        </BodyXS>
      )}
      {props.error && errorMessage && (
        <BodyXS color={'text-moment-red-600'} className="mt-1 pl-[1px]">
          {errorMessage}
        </BodyXS>
      )}
    </div>
  )
})

Input.displayName = 'Input'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default Input
